import React, { useMemo, useState } from 'react';
import { CATEGORIAS } from '../../utils/parseBankStatement';

// ── Paleta validada (colorblind-safe, orden fijo por slot) ────────────────────
// Mismos 8 tonos categóricos que el resto de gráficas de la app deberían usar;
// aquí solo se necesitan un puñado para las categorías de ingreso/gasto.
const SLOT_HEX = {
  1: '#2a78d6', // azul
  2: '#eb6834', // naranja
  3: '#1baf7a', // aqua
  4: '#eda100', // amarillo
  5: '#e87ba4', // magenta
  6: '#008300', // verde
  7: '#4a3aa7', // violeta
  8: '#e34948', // rojo
};
const GOOD    = '#0ca30c';
const CRITICAL = '#d03b3b';
const INK      = '#0b0b0b';
const INK_SOFT = '#52514e';
const MUTED    = '#898781';
const GRID     = '#e1e0d9';

const fmtEUR = (n) => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtEURCompact = (n) => {
  const abs = Math.abs(Number(n || 0));
  if (abs >= 1000) return (n / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 }) + 'k €';
  return fmtEUR(n);
};
const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }).replace('.', '');
};

// ── Stat tile ──────────────────────────────────────────────────────────────────
function StatTile({ label, value, sub, tone = 'neutral' }) {
  const toneCls = tone === 'good' ? 'text-[--good]' : tone === 'critical' ? 'text-[--critical]' : 'text-gray-900';
  const color = tone === 'good' ? GOOD : tone === 'critical' ? CRITICAL : INK;
  return (
    <div className="bg-white rounded-2xl shadow border p-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${toneCls}`} style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

// ── Gráfico de líneas: evolución del saldo ──────────────────────────────────────
function SaldoLineChart({ points }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 220, PAD_L = 56, PAD_R = 16, PAD_T = 16, PAD_B = 28;
  const plotW = W - PAD_L - PAD_R, plotH = H - PAD_T - PAD_B;

  if (points.length < 2) {
    return <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">No hay suficientes movimientos para dibujar la evolución.</div>;
  }

  const vals = points.map(p => p.saldo);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = (max - min) || 1;
  const yFor = (v) => PAD_T + plotH - ((v - min) / range) * plotH;
  const xFor = (i) => PAD_L + (i / (points.length - 1)) * plotW;

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(p.saldo).toFixed(1)}`).join(' ');
  const areaPath = `${path} L ${xFor(points.length - 1).toFixed(1)} ${(PAD_T + plotH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(PAD_T + plotH).toFixed(1)} Z`;

  const ticksY = 4;
  const yTicks = Array.from({ length: ticksY + 1 }, (_, i) => min + (range * i) / ticksY);

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((x - PAD_L) / plotW) * (points.length - 1));
    if (idx >= 0 && idx < points.length) setHover(idx);
  };

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" onMouseMove={handleMove} onMouseLeave={() => setHover(null)}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yFor(v)} y2={yFor(v)} stroke={GRID} strokeWidth="1" />
            <text x={PAD_L - 8} y={yFor(v) + 3} fontSize="10" fill={MUTED} textAnchor="end">{fmtEURCompact(v)}</text>
          </g>
        ))}
        <path d={areaPath} fill={SLOT_HEX[1]} opacity="0.08" />
        <path d={path} fill="none" stroke={SLOT_HEX[1]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Primer y último punto de fecha en el eje X */}
        <text x={xFor(0)} y={H - 8} fontSize="10" fill={MUTED} textAnchor="start">
          {new Date(points[0].fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
        </text>
        <text x={xFor(points.length - 1)} y={H - 8} fontSize="10" fill={MUTED} textAnchor="end">
          {new Date(points[points.length - 1].fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
        </text>

        {hover != null && (
          <g>
            <line x1={xFor(hover)} x2={xFor(hover)} y1={PAD_T} y2={PAD_T + plotH} stroke={MUTED} strokeWidth="1" strokeDasharray="3,3" />
            <circle cx={xFor(hover)} cy={yFor(points[hover].saldo)} r="4" fill={SLOT_HEX[1]} stroke="#fff" strokeWidth="1.5" />
          </g>
        )}
      </svg>
      {hover != null && (
        <div
          className="absolute bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 pointer-events-none shadow-lg"
          style={{ left: `${(xFor(hover) / W) * 100}%`, top: 4, transform: 'translateX(-50%)' }}
        >
          <div className="font-semibold">{fmtEUR(points[hover].saldo)}</div>
          <div className="text-gray-300">{new Date(points[hover].fecha).toLocaleDateString('es-ES')}</div>
        </div>
      )}
    </div>
  );
}

// ── Barras horizontales: desglose por categoría ─────────────────────────────────
function HBarBreakdown({ items }) {
  const [hover, setHover] = useState(null);
  if (!items.length) return <div className="py-8 text-center text-sm text-gray-400">Sin movimientos en este período.</div>;
  const max = Math.max(...items.map(i => i.total));
  const total = items.reduce((s, i) => s + i.total, 0);

  return (
    <div className="space-y-2.5">
      {items.map((it, i) => {
        const pct = max > 0 ? (it.total / max) * 100 : 0;
        const share = total > 0 ? (it.total / total) * 100 : 0;
        return (
          <div key={it.categoria} className="group" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-gray-700 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: SLOT_HEX[it.slot] }} />
                {it.label}
              </span>
              <span className="tabular-nums text-gray-500">
                {fmtEUR(it.total)} <span className="text-gray-400">· {share.toFixed(0)}%</span>
              </span>
            </div>
            <div className="h-3 rounded-full bg-gray-100 overflow-hidden relative">
              <div
                className="h-full rounded-full transition-[width]"
                style={{ width: `${Math.max(pct, 2)}%`, background: SLOT_HEX[it.slot], opacity: hover === i ? 1 : 0.9 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Barras agrupadas: ingresos vs gastos por mes ────────────────────────────────
function IngresosGastosPorMes({ meses }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 240, PAD_L = 56, PAD_R = 16, PAD_T = 16, PAD_B = 32;
  const plotW = W - PAD_L - PAD_R, plotH = H - PAD_T - PAD_B;

  if (!meses.length) return <div className="py-8 text-center text-sm text-gray-400">Sin movimientos en este período.</div>;

  const maxVal = Math.max(1, ...meses.map(m => Math.max(m.ingresos, m.gastos)));
  const groupW = plotW / meses.length;
  const barW = Math.min(22, groupW * 0.32);
  const yFor = (v) => PAD_T + plotH - (v / maxVal) * plotH;

  const ticksY = 4;
  const yTicks = Array.from({ length: ticksY + 1 }, (_, i) => (maxVal * i) / ticksY);

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yFor(v)} y2={yFor(v)} stroke={GRID} strokeWidth="1" />
            <text x={PAD_L - 8} y={yFor(v) + 3} fontSize="10" fill={MUTED} textAnchor="end">{fmtEURCompact(v)}</text>
          </g>
        ))}
        {meses.map((m, i) => {
          const cx = PAD_L + groupW * i + groupW / 2;
          const xIn = cx - barW - 2, xOut = cx + 2;
          const isHover = hover === i;
          return (
            <g key={m.mes}
               onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
               style={{ cursor: 'pointer' }}>
              <rect x={xIn} y={yFor(m.ingresos)} width={barW} height={Math.max(plotH - (yFor(m.ingresos) - PAD_T), 0)}
                rx="3" fill={GOOD} opacity={isHover ? 1 : 0.85} />
              <rect x={xOut} y={yFor(m.gastos)} width={barW} height={Math.max(plotH - (yFor(m.gastos) - PAD_T), 0)}
                rx="3" fill={CRITICAL} opacity={isHover ? 1 : 0.85} />
              <text x={cx} y={H - 10} fontSize="10" fill={MUTED} textAnchor="middle">{monthLabel(m.mes)}</text>
              <rect x={PAD_L + groupW * i} y={PAD_T} width={groupW} height={plotH} fill="transparent" />
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <div className="absolute bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 pointer-events-none shadow-lg"
          style={{ left: `${((PAD_L + (plotW / meses.length) * (hover + 0.5)) / W) * 100}%`, top: 4, transform: 'translateX(-50%)' }}>
          <div className="font-semibold mb-0.5">{monthLabel(meses[hover].mes)}</div>
          <div style={{ color: '#6ee56e' }}>Ingresos: {fmtEUR(meses[hover].ingresos)}</div>
          <div style={{ color: '#f29a9a' }}>Gastos: {fmtEUR(meses[hover].gastos)}</div>
        </div>
      )}
      <div className="flex items-center gap-4 justify-center text-xs text-gray-500 mt-1">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: GOOD }} />Ingresos</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: CRITICAL }} />Gastos</span>
      </div>
    </div>
  );
}

// ── Vista principal del dashboard ────────────────────────────────────────────────
const PERIODOS = [
  { id: 'mes', label: 'Este mes' },
  { id: '3m', label: 'Últimos 3 meses' },
  { id: 'ano', label: 'Este año' },
  { id: 'todo', label: 'Todo' },
  { id: 'rango', label: 'Rango personalizado' },
];

export default function BancoDashboard({ movimientos }) {
  const [periodo, setPeriodo] = useState('3m');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const enPeriodo = useMemo(() => {
    if (!movimientos.length) return [];
    const now = new Date();
    let start = null, end = null;
    if (periodo === 'mes') start = new Date(now.getFullYear(), now.getMonth(), 1);
    if (periodo === '3m') { start = new Date(now.getFullYear(), now.getMonth() - 2, 1); }
    if (periodo === 'ano') start = new Date(now.getFullYear(), 0, 1);
    if (periodo === 'rango') {
      start = fechaDesde ? new Date(fechaDesde) : null;
      end = fechaHasta ? new Date(`${fechaHasta}T23:59:59.999`) : null;
    }
    return movimientos.filter(m => {
      const f = new Date(m.fecha);
      return (!start || f >= start) && (!end || f <= end);
    });
  }, [movimientos, periodo, fechaDesde, fechaHasta]);

  // Movimientos ordenados cronológicamente (para la línea de saldo) — la lista
  // principal viene ordenada desc, aquí se necesita asc.
  const cronologico = useMemo(() =>
    [...enPeriodo].filter(m => m.saldo != null).sort((a, b) => new Date(a.fecha) - new Date(b.fecha)),
  [enPeriodo]);

  const saldoActual = movimientos[0]?.saldo ?? null; // movimientos viene ordenado desc por fecha

  const resumen = useMemo(() => {
    let ingresos = 0, gastos = 0, interno = 0;
    const porCategoriaGasto = {};
    const porCategoriaIngreso = {};
    for (const m of enPeriodo) {
      const cat = m.categoria && CATEGORIAS[m.categoria] ? { categoria: m.categoria, ...CATEGORIAS[m.categoria] } : null;
      const tipo = cat?.tipo || (m.importe >= 0 ? 'ingreso' : 'gasto');
      if (tipo === 'interno') { interno += Math.abs(m.importe); continue; }
      if (tipo === 'ingreso') {
        ingresos += m.importe;
        if (cat) {
          const key = cat.categoria;
          porCategoriaIngreso[key] = porCategoriaIngreso[key] || { categoria: key, label: cat.label, slot: cat.slot, total: 0 };
          porCategoriaIngreso[key].total += m.importe;
        }
      } else {
        gastos += Math.abs(m.importe);
        if (cat) {
          const key = cat.categoria;
          porCategoriaGasto[key] = porCategoriaGasto[key] || { categoria: key, label: cat.label, slot: cat.slot, total: 0 };
          porCategoriaGasto[key].total += Math.abs(m.importe);
        }
      }
    }
    return {
      ingresos, gastos, interno,
      neto: ingresos - gastos,
      gastosPorCategoria: Object.values(porCategoriaGasto).sort((a, b) => b.total - a.total),
      ingresosPorCategoria: Object.values(porCategoriaIngreso).sort((a, b) => b.total - a.total),
    };
  }, [enPeriodo]);

  const porMes = useMemo(() => {
    const map = {};
    for (const m of enPeriodo) {
      const cat = m.categoria && CATEGORIAS[m.categoria] ? CATEGORIAS[m.categoria] : null;
      const tipo = cat?.tipo || (m.importe >= 0 ? 'ingreso' : 'gasto');
      if (tipo === 'interno') continue;
      const mes = m.fecha.slice(0, 7);
      if (!map[mes]) map[mes] = { mes, ingresos: 0, gastos: 0 };
      if (tipo === 'ingreso') map[mes].ingresos += m.importe;
      else map[mes].gastos += Math.abs(m.importe);
    }
    return Object.values(map).sort((a, b) => a.mes.localeCompare(b.mes));
  }, [enPeriodo]);

  const topGastos = useMemo(() =>
    [...enPeriodo].filter(m => m.importe < 0 && (!m.categoria || CATEGORIAS[m.categoria]?.tipo !== 'interno'))
      .sort((a, b) => a.importe - b.importe).slice(0, 8),
  [enPeriodo]);

  if (!movimientos.length) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border p-12 text-center">
        <div className="text-5xl mb-3">📊</div>
        <p className="text-gray-500 font-medium">Sin movimientos importados todavía</p>
        <p className="text-gray-400 text-sm mt-1">Importa el extracto (PDF o CSV) para ver el dashboard financiero</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selector de período */}
      <div className="flex flex-wrap items-center gap-1.5">
        {PERIODOS.map(p => (
          <button key={p.id} onClick={() => setPeriodo(p.id)}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              periodo === p.id ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}>
            {p.label}
          </button>
        ))}
        {periodo === 'rango' && (
          <div className="flex items-center gap-1.5 ml-1">
            <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded-lg text-gray-600" />
            <span className="text-xs text-gray-400">a</span>
            <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded-lg text-gray-600" />
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Saldo actual" value={saldoActual != null ? fmtEUR(saldoActual) : '—'} sub="A fecha del último movimiento" />
        <StatTile label="Ingresos del período" value={fmtEUR(resumen.ingresos)} tone="good" sub={`${resumen.ingresosPorCategoria.length} orígenes`} />
        <StatTile label="Gastos del período" value={fmtEUR(resumen.gastos)} tone="critical" sub={`${resumen.gastosPorCategoria.length} categorías`} />
        <StatTile label="Resultado neto" value={fmtEUR(resumen.neto)} tone={resumen.neto >= 0 ? 'good' : 'critical'}
          sub={resumen.interno > 0 ? `${fmtEUR(resumen.interno)} en traspasos internos excluidos` : 'Ingresos − Gastos'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Evolución de saldo */}
        <div className="bg-white rounded-2xl shadow-lg border p-5 lg:col-span-2">
          <h3 className="font-bold text-gray-800 mb-3">Evolución del saldo</h3>
          <SaldoLineChart points={cronologico} />
        </div>

        {/* Ingresos vs gastos por mes */}
        <div className="bg-white rounded-2xl shadow-lg border p-5 lg:col-span-2">
          <h3 className="font-bold text-gray-800 mb-3">Ingresos vs. gastos por mes</h3>
          <IngresosGastosPorMes meses={porMes} />
        </div>

        {/* Desglose de gastos */}
        <div className="bg-white rounded-2xl shadow-lg border p-5">
          <h3 className="font-bold text-gray-800 mb-3">Gastos por categoría</h3>
          <HBarBreakdown items={resumen.gastosPorCategoria} />
        </div>

        {/* Desglose de ingresos */}
        <div className="bg-white rounded-2xl shadow-lg border p-5">
          <h3 className="font-bold text-gray-800 mb-3">Ingresos por origen</h3>
          <HBarBreakdown items={resumen.ingresosPorCategoria} />
        </div>

        {/* Top gastos */}
        <div className="bg-white rounded-2xl shadow-lg border p-5 lg:col-span-2">
          <h3 className="font-bold text-gray-800 mb-3">Mayores salidas del período</h3>
          {topGastos.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Sin gastos en este período.</p>
          ) : (
            <div className="space-y-2">
              {topGastos.map((m, i) => {
                const cat = m.categoria && CATEGORIAS[m.categoria];
                return (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {cat && <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: SLOT_HEX[cat.slot] }} />}
                      <span className="text-gray-700 truncate">{m.concepto}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-gray-400">{new Date(m.fecha).toLocaleDateString('es-ES')}</span>
                      <span className="font-bold text-red-600 tabular-nums">{fmtEUR(m.importe)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
